import { ArrowDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

export interface FilterPopoverTriggerProps {
  /** Trigger text (e.g., column name, filter label). */
  label: string;
  /** Whether the popover is currently open — drives aria-expanded + chevron rotation. */
  isOpen: boolean;
  /** Click handler — typically toggles the popover. */
  onClick: () => void;
  /** Optional leading glyph rendered before the label. */
  leadingIcon?: React.ReactNode;
  /** Optional className appended to the root button. */
  className?: string;
}

/**
 * FilterPopoverTrigger — small inline `text + chevron` trigger per Figma
 * `289:48315`/`289:48600`/`289:49244`/`289:49374`/`289:41026`. Used as the
 * anchor for column/widget filter popovers.
 *
 * Variants: Default (chevron pointing down), Open (chevron rotated 180°).
 * ARIA: `aria-haspopup="true"`, `aria-expanded={isOpen}`.
 */
export const FilterPopoverTrigger = React.forwardRef<HTMLButtonElement, FilterPopoverTriggerProps>(
  ({ label, isOpen, onClick, leadingIcon, className }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 bg-transparent border-0 p-0 outline-none',
          'font-outfit text-[13px] font-normal leading-[1.3] text-dawn-black',
          'hover:opacity-80 focus-visible:opacity-80 cursor-pointer',
          className
        )}
      >
        {leadingIcon ? (
          <span className="inline-flex shrink-0 items-center">{leadingIcon}</span>
        ) : null}
        <span>{label}</span>
        <ArrowDown
          data-testid="filter-popover-chevron"
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
    );
  }
);

FilterPopoverTrigger.displayName = 'FilterPopoverTrigger';
